import _ from 'lodash';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CampaignService } from '../../services/campaign.service';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { ImageService } from 'app/core/services/image.service';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';

import { select, Store } from '@ngrx/store';
import { selectCampaignSelected } from '../../features/campaign.selectors';
import { CampaignFormService } from '../../services/campaign-form.service';
import { TicketingBasketRaffleItemSchema } from '../../structure/ticketing';
import { createBasketRaffleItem, updateBasketRaffleItem } from '../../features/basketRaffle.actions';
import { ToastService } from 'app/toast/services/toast.service';
import { ChooseImageModalService } from 'app/core/services/choose-image-modal.service';

@Component({
  selector: 'app-campaign-basket-raffle-item',
  templateUrl: './campaign-basket-raffle-item.component.html',
  styleUrls: ['./campaign-basket-raffle-item.component.scss']
})
export class CampaignBasketRaffleItemComponent implements OnInit, OnDestroy {
  @Input() formGroupCampaignTypeData: any = null;
  @Input() selected: any = null;

  @Output() onChangeData: EventEmitter<any> = new EventEmitter();

  public campaign$ = this.store.pipe(
    select(selectCampaignSelected)
  );
  public campaign = null;

  public basketRaffleItem: UntypedFormGroup = null;

  public value: any = null;

  public takeUntilSubject: Subject<any> = new Subject();

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private campaignService: CampaignService,
    private campaignFormService: CampaignFormService,
    private imageService: ImageService,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private chooseImageModalService: ChooseImageModalService,
  ) { }

  ngOnInit(): void {
    const { selected } = this;

    this.campaign$.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(campaign => {
      this.campaign = campaign;
    });

    if(this.selected) {
      const image = selected.get('image').value === '/images/org_logo_default_v2.svg' ?
        '/images/blank_2x1.png':
        selected.get('image').value;
      selected.get('imageDataUrl').setValue(image);
      selected.get('image').setValue(image);

      this.basketRaffleItem = this.selected;
      this.value = _.cloneDeep(this.selected.value);
    } else {
      this.basketRaffleItem = this.fb.group({});
      this.campaignService.generateForm(this.basketRaffleItem, TicketingBasketRaffleItemSchema);
    }
    this.reNgOnInit();
  }

  reNgOnInit() {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();

    this.takeUntilSubject = new Subject();

    this.basketRaffleItem.get('imageFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.imageService.cropper({
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id: 'basket-raffle-item-image',
        name: value.name,
        type: value.type,
      });
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'basket-raffle-item-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      this.basketRaffleItem.get('imageDataUrl').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'basket-raffle-item-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImage();
      } else {
        this.basketRaffleItem.get('image').setValue(result.imageUrl);
      }
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const selected = _.get(changes, 'selected.currentValue', this.selected);
    if(selected) {
      const image = selected.get('image').value === '/images/org_logo_default_v2.svg' ?
        '/images/blank_2x1.png':
        selected.get('image').value;
      selected.get('imageDataUrl').setValue(image);
      selected.get('image').setValue(image);

      this.basketRaffleItem = this.selected;
      this.value = _.cloneDeep(this.selected.value);
    }

    if(this.basketRaffleItem) {
      this.reNgOnInit();
    }
  }

  ngOnDestroy() {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
  }

  onCancel() {
    if(this.selected) {
      this.basketRaffleItem.reset(this.value);
    }
    this.onChangeData.next(false);
  }

  saveCampaignBasketRaffleItem() {
    const { campaign, basketRaffleItem, selected } = this;

    const name = basketRaffleItem.get('name');
    const trimedName = _.trim(name.value);
    name.setValue(trimedName);

    if(basketRaffleItem.invalid) {
      basketRaffleItem.markAllAsTouched();
      this.campaignFormService.isNotValid();
      return false;
    }

    if(selected) {
      const image = selected.get('image').value === '/images/blank_2x1.png' ?
        '':
        selected.get('image').value;
      selected.get('image').setValue(image);

      const campaignId = _.get(campaign, 'id', null);
      const basketRaffleItemValue = this.campaignService.generateRequestObject(selected, TicketingBasketRaffleItemSchema);
      this.store.dispatch({
        type: updateBasketRaffleItem.type,
        payload: { campaignId, basketItem: basketRaffleItemValue }
      });
      return this.onCancel();
    } else {
      this.addCampaignBasketRaffleItem();
    }
  }

  addCampaignBasketRaffleItem() {
    const { campaign, basketRaffleItem, basketRaffleItem: { value } } = this;

    const image = value.image === '/images/blank_2x1.png' ? '' : value.image;
    basketRaffleItem.get('image').setValue(image);

    const group = this.fb.group({});
    this.campaignService.generateForm(group, TicketingBasketRaffleItemSchema);
    this.campaignService.initializeForm(group, this.basketRaffleItem.value, TicketingBasketRaffleItemSchema);

    basketRaffleItem.reset();
    basketRaffleItem.get('image').setValue("/images/blank_2x1.png");

    const campaignId = _.get(campaign, 'id', null);
    const basketRaffleItemValue = this.campaignService.generateRequestObject(group, TicketingBasketRaffleItemSchema);

    delete basketRaffleItemValue['id'];
    this.store.dispatch({
      type: createBasketRaffleItem.type,
      payload: { campaignId, basketItem: basketRaffleItemValue }
    });
    return this.onCancel();
  }

  openChooseImageModal(type = 0) { // 0 for 2x1, 1 for 1x1
    const params = {
      start: 0,
      returnCount: 40,
      filterBy:[type]
    }

    this.campaignService.getOrganizationImages(params).pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe((imageList: any)=> {
      const list = imageList || [];
      const images = list.filter((image) => image.type === type)
      const modalRef = this.chooseImageModalService.open({ images, type });
    
      modalRef.onClose.pipe(
        takeUntil(this.takeUntilSubject)
      ).subscribe((imageUrl) =>{
        if(imageUrl) {
          this.basketRaffleItem.get(`imageDataUrl`).setValue(imageUrl);
          this.basketRaffleItem.get(`image`).setValue(imageUrl);

          this.toastService.show('success', 'Added Successfully!', {delay: 2000});
        }
        this.chooseImageModalService.modalRef = null;
      })
    })
  }

  resetImage() {
    this.basketRaffleItem.get('imageFile').setValue(null);
    this.basketRaffleItem.get('imageDataUrl').setValue('/images/blank_2x1.png');
    this.basketRaffleItem.get('image').setValue('/images/blank_2x1.png');
  }
}
